import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Platform,
  Alert,
  Animated,
} from 'react-native';
import { Audio } from 'expo-av';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme } from '../components/ui';
import { transcribeAudio } from '../services/audioTranscriptionService';

interface AudioRecorderProps {
  onTranscription: (text: string) => void;
  onRecordingStateChange?: (isRecording: boolean, isStarting: boolean, isProcessing: boolean) => void;
  placeholder?: string;
  maxDuration?: number; // in seconds
  disabled?: boolean;
}

export interface AudioRecorderRef {
  isRecording: boolean;
  stopRecording: () => void;
}

const AudioRecorder = forwardRef<AudioRecorderRef, AudioRecorderProps>(({
  onTranscription,
  onRecordingStateChange,
  placeholder = "Tap to record",
  maxDuration = 60,
  disabled = false,
}, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [duration, setDuration] = useState(0);
  const recordingRef = useRef<Audio.Recording | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pulseAnim = useRef(new Animated.Value(1)).current;

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    isRecording,
    stopRecording,
  }));

  // Pulse animation for starting state
  useEffect(() => {
    if (isStarting) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.1,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    } else {
      pulseAnim.setValue(1);
    }
  }, [isStarting, pulseAnim]);

  const startRecording = async () => {
    try {
      setIsStarting(true);
      onRecordingStateChange?.(false, true, false);
      
      // Request permissions
      const { status } = await Audio.requestPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission required', 'Please grant microphone permission to use voice input.');
        setIsStarting(false);
        onRecordingStateChange?.(false, false, false);
        return;
      }

      // Configure audio mode for recording
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
        staysActiveInBackground: true,
      });

      // Start recording
      const { recording } = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY
      );
      recordingRef.current = recording;
      setIsStarting(false);
      setIsRecording(true);
      onRecordingStateChange?.(true, false, false);
      setDuration(0);

      // Start duration counter
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => {
          const newDuration = prev + 1;
          if (newDuration >= maxDuration) {
            stopRecording();
          }
          return newDuration;
        });
      }, 1000);

    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsStarting(false);
      onRecordingStateChange?.(false, false, false);
      Alert.alert('Error', 'Failed to start recording. Please try again.');
    }
  };

  const stopRecording = async () => {
    try {
      if (!recordingRef.current) return;

      setIsRecording(false);
      onRecordingStateChange?.(false, false, false);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      await recordingRef.current.stopAndUnloadAsync();
      const uri = recordingRef.current.getURI();
      recordingRef.current = null;

      // Restore audio mode for playback after recording
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        playsInSilentModeIOS: true,
        staysActiveInBackground: true,
        shouldDuckAndroid: true,
      });

      if (uri) {
        await processAudioFile(uri);
      }
    } catch (error) {
      console.error('Failed to stop recording:', error);
      Alert.alert('Error', 'Failed to process recording. Please try again.');
    }
  };

  const processAudioFile = async (uri: string) => {
    setIsProcessing(true);
    onRecordingStateChange?.(false, false, true);
    
    try {
      console.log('🎤 Processing audio file:', uri);
      
      // Use the transcription service
      const transcribedText = await transcribeAudio(uri);
      
      if (transcribedText && transcribedText.trim()) {
        console.log('✅ Transcription successful');
        onTranscription(transcribedText.trim());
      } else {
        throw new Error('Empty transcription result');
      }
      
    } catch (error) {
      console.error('❌ Transcription failed:', error);
      
      // Show user-friendly error message
      const errorMessage = (error instanceof Error ? error.message : String(error)) || 'Failed to process audio. Please try again or type your response.';
      Alert.alert('Audio Processing Error', errorMessage);
      
    } finally {
      setIsProcessing(false);
      onRecordingStateChange?.(false, false, false);
      setDuration(0);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusText = (): string => {
    if (isProcessing) return 'Processing audio...';
    if (isRecording) return `Recording ${formatDuration(duration)}`;
    if (isStarting) return 'Starting...';
    return placeholder;
  };

  const getIcon = (): keyof typeof Ionicons.glyphMap => {
    if (isProcessing) return 'hourglass';
    if (isRecording) return 'stop';
    if (isStarting) return 'mic';
    return 'mic';
  };

  const getIconColor = (): string => {
    if (disabled) return Theme.colors.textDisabled;
    if (isRecording) return Theme.colors.error;
    if (isProcessing) return Theme.colors.warning;
    if (isStarting) return Theme.colors.primary;
    return Theme.colors.primary;
  };

  const shouldShowSpinner = (): boolean => {
    return isProcessing || isStarting;
  };

  const handlePress = () => {
    if (disabled || isProcessing || isStarting) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Pressable
      style={[
        styles.container,
        disabled && styles.containerDisabled,
        isRecording && styles.containerRecording,
        isStarting && styles.containerStarting,
      ]}
      onPress={handlePress}
      disabled={disabled || isProcessing || isStarting}
    >
      <View style={styles.content}>
        <Animated.View style={[
          styles.iconContainer,
          isStarting && styles.iconContainerStarting,
          isStarting && { transform: [{ scale: pulseAnim }] },
        ]}>
          {shouldShowSpinner() ? (
            <ActivityIndicator size="small" color={getIconColor()} />
          ) : (
            <Ionicons name={getIcon()} size={24} color={getIconColor()} />
          )}
        </Animated.View>
        <Text 
          variant="body2" 
          style={disabled
            ? { ...styles.statusText, ...styles.statusTextDisabled }
            : styles.statusText
          }
        >
          {getStatusText()}
        </Text>
        
        {maxDuration > 0 && isRecording && (
          <Text variant="caption" color="secondary" style={styles.maxDurationText}>
            Max {maxDuration}s
          </Text>
        )}
      </View>
      
      {/* Progress indicator for recording */}
      {isRecording && maxDuration > 0 && (
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View 
              style={[
                styles.progressFill, 
                { width: `${(duration / maxDuration) * 100}%` }
              ]} 
            />
          </View>
        </View>
      )}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  container: {
    marginTop: 16,
    marginBottom: 8,
  },
  containerDisabled: {
    backgroundColor: Theme.colors.surfaceSecondary,
    opacity: 0.6,
  },
  containerRecording: {
    backgroundColor: Theme.colors.primaryLight,
  },
  containerStarting: {
    backgroundColor: Theme.colors.primaryLight,
    opacity: 0.8,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerStarting: {
    backgroundColor: Theme.colors.primaryLight,
    borderWidth: 2,
    borderColor: Theme.colors.primary,
  },
  statusText: {
    flex: 1,
    fontSize: 18,
    lineHeight: 20,
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.regular,
    marginBottom: 4,
  },
  statusTextDisabled: {
    color: Theme.colors.textDisabled,
  },
  maxDurationText: {
    fontSize: 14,
    lineHeight: 18,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.regular,
  },
  progressContainer: {
    height: 4,
    backgroundColor: Theme.colors.border,
    borderRadius: 2,
    marginTop: 16,
    marginHorizontal: 24,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: Theme.colors.border,
    borderRadius: 2,
  },
  progressFill: {
    height: '100%',
    backgroundColor: Theme.colors.primary,
    borderRadius: 2,
  },
});

export default AudioRecorder; 